import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import PaymentStatusMonths from './PaymentStatusMonths';
import PaymentFacilities from './PaymentFacilities';
import PaymentClaims from './PaymentClaims';
import PaymentBatches from './PaymentBatches';
import PaymentBatchClaims from './PaymentBatchClaims';
import PaymentStageTabs from './PaymentStageTabs';
import DrilldownCrumbs from './DrilldownCrumbs';
import dayjs from 'dayjs';
import lib from '../../../assets/utils/lib';
import './PaymentHierarchy.css';
import './PaymentDrilldown.css';

/**
 * PaymentHierarchy - Main container for payment claims navigation
 * Manages the status-first navigation flow:
 * Dashboard → Status → Months → Facilities → Claims/Batches → Batch Claims
 */
const STATUS_LABELS = {
  0: 'Pending CO',
  1: 'Pending HM',
  2: 'Pending CFO',
  3: 'Paid'
};

const PaymentHierarchy = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state;
  const [searchParams, setSearchParams] = useSearchParams();
  const PAYMENT_MONTHS_PARAMS = ['paymentMonthsSearch', 'paymentMonthsPage', 'paymentMonthsPageSize'];
  const PAYMENT_FACILITIES_PARAMS = ['paymentFacilitiesSearch', 'paymentFacilitiesPage', 'paymentFacilitiesPageSize'];
  const PAYMENT_CLAIMS_PARAMS = ['paymentClaimsSearch', 'paymentClaimsPage', 'paymentClaimsPageSize'];
  const PAYMENT_SCOPED_PARAMS = [
    ...PAYMENT_MONTHS_PARAMS,
    ...PAYMENT_FACILITIES_PARAMS,
    ...PAYMENT_CLAIMS_PARAMS,
  ];
  
  // Helper to get facility ID from various formats
  const getFacilityId = (facility) => {
    if (!facility) return null;
    if (typeof facility === 'object') return facility.facility_id || facility.id || null;
    return facility;
  };

  const getCurrentLevel = () => {
    if (currentStatus === null || currentStatus === undefined) return 'dashboard';
    if (!currentMonth) return 'months';
    if (!currentFacility) return 'facilities';
    if (currentStatus === 3 && currentBatch) return 'batch_claims';
    if (currentStatus === 3) return 'batches';
    return 'claims';
  };

  const pruneScopedParams = (params, targetLevel) => {
    const allowedByLevel = {
      dashboard: [],
      months: PAYMENT_MONTHS_PARAMS,
      facilities: PAYMENT_FACILITIES_PARAMS,
      claims: PAYMENT_CLAIMS_PARAMS,
      batches: [],
      batch_claims: [],
    };
    const allowed = new Set(allowedByLevel[targetLevel] || []);

    PAYMENT_SCOPED_PARAMS.forEach((key) => {
      if (!allowed.has(key)) {
        params.delete(key);
      }
    });

    // Clean up legacy shared keys so old links cannot leak state.
    params.delete('search');
    params.delete('page');
    params.delete('pageSize');
  };
  
  // Initialize from URL params first, then fallback to state.
  // The stage tab strip is always visible now (no standalone dashboard page),
  // so default to Pending CO rather than leaving the stage unset.
  const [currentStatus, setCurrentStatus] = useState(() => {
    const statusParam = searchParams.get('status');
    if (statusParam !== null) {
      return parseInt(statusParam, 10);
    }
    return state?.status ?? 0;
  });
  
  const [currentMonth, setCurrentMonth] = useState(() => {
    const monthParam = searchParams.get('month');
    if (monthParam) {
      return monthParam;
    }
    return state?.month || null;
  });
  
  const [currentFacility, setCurrentFacility] = useState(() => {
    const facilityParam = searchParams.get('facility');
    if (facilityParam) {
      // Facility ID from URL - will be fetched in useEffect if needed
      return parseInt(facilityParam, 10);
    }
    // If facility is an object, use it directly
    if (state?.facility && typeof state.facility === 'object') {
      return state.facility;
    }
    return state?.facility || null;
  });
  
  const [currentBatch, setCurrentBatch] = useState(() => {
    const batchParam = searchParams.get('batch');
    if (batchParam) {
      return batchParam;
    }
    return state?.batch || null;
  });
  
  const [breadcrumbs, setBreadcrumbs] = useState([]);

  // Navigation handlers
  const handleStatusSelect = (statusCode) => {
    setCurrentStatus(statusCode);
    setCurrentMonth(null);
    setCurrentFacility(null);
    setCurrentBatch(null);
  };

  // Helper to get month key from various formats
  const getMonthKey = (month) => {
    if (!month) return null;
    if (typeof month === 'string') return month;
    return month.month || month.key || null;
  };

  // Update URL when navigation state changes
  useEffect(() => {
    const monthKey = getMonthKey(currentMonth);
    const facilityId = getFacilityId(currentFacility);
    const currentLevel = getCurrentLevel();
    
    // Start with existing params to preserve search and other params
    const params = new URLSearchParams(searchParams);
    
    if (currentStatus !== null) {
      params.set('status', currentStatus.toString());
    } else {
      params.delete('status');
    }
    
    if (monthKey) {
      params.set('month', monthKey);
    } else {
      params.delete('month');
    }
    
    if (facilityId) {
      params.set('facility', facilityId.toString());
    } else {
      params.delete('facility');
    }
    
    if (currentBatch) {
      params.set('batch', currentBatch);
    } else {
      params.delete('batch');
    }

    pruneScopedParams(params, currentLevel);
    
    // Only update URL if params have changed (avoid infinite loop)
    const currentParams = searchParams.toString();
    const newParams = params.toString();
    
    if (currentParams !== newParams) {
      setSearchParams(params, { replace: true });
    }
  }, [currentStatus, currentMonth, currentFacility, currentBatch, setSearchParams, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect to restore facility object when we have month and facility_id
  useEffect(() => {
    // If we have status, month and facility is a number (facility_id), fetch the facility object
    if (currentStatus !== null && currentMonth && currentFacility && typeof currentFacility === 'number') {
      const fetchFacility = async () => {
        try {
          const monthKey = getMonthKey(currentMonth);
          const response = await lib.getPaymentStatusFacilities(currentStatus, monthKey, { page: 1, pageSize: 100 });
          
          if (response?.data?.error === 0) {
            const facilities = Array.isArray(response.data.data) ? response.data.data : [];
            const facility = facilities.find(f => Number(f.facility_id) === Number(currentFacility));
            if (facility) {
              setCurrentFacility(facility);
            }
          }
        } catch (error) {
          console.error('Error fetching facility:', error);
        }
      };
      
      fetchFacility();
    }
  }, [currentStatus, currentMonth, currentFacility]);

  const handleMonthSelect = (month) => {
    // Store the month key (string) for API calls, but keep the full object for display
    const monthKey = getMonthKey(month);
    setCurrentMonth(monthKey || month);
    setCurrentFacility(null);
    setCurrentBatch(null);
  };

  const handleFacilitySelect = (facility) => {
    setCurrentFacility(facility);
    setCurrentBatch(null);
  };

  const handleBatchSelect = (batch) => {
    setCurrentBatch(batch.payment_batch);
  };

  const handleClaimClick = (claim) => {
    // Navigate to claims filing page
    const encounterId = claim.checkin_id || claim.claims_id;
    const facilityId = currentFacility?.facility_id || currentFacility;
    const segment = currentStatus;
    
    if (encounterId && facilityId) {
      navigate(`/claims/filings/?encounter=${encodeURIComponent(encounterId)}`, {
        state: { 
          facility: typeof currentFacility === 'object' ? currentFacility : facilityId,
          segment, 
          claim, 
          month: currentMonth 
        }
      });
    }
  };

  // Back navigation handlers
  const handleBackToMonths = () => {
    setCurrentMonth(null);
    setCurrentFacility(null);
    setCurrentBatch(null);
  };

  const handleBackToFacilities = () => {
    setCurrentFacility(null);
    setCurrentBatch(null);
  };

  const handleBackToBatches = () => {
    setCurrentBatch(null);
  };

  // Update breadcrumbs when navigation changes
  useEffect(() => {
    const crumbs = [];

    if (currentStatus !== null) {
      crumbs.push({
        label: STATUS_LABELS[currentStatus] || `Status ${currentStatus}`,
        level: 'months',
        onClick: handleBackToMonths,
      });
    }

    if (currentMonth) {
      const monthLabel = typeof currentMonth === 'string' 
        ? dayjs(currentMonth + '-01').format('MMMM YYYY')
        : currentMonth.month_label || dayjs(currentMonth.month + '-01').format('MMMM YYYY');
      crumbs.push({
        label: `${monthLabel} (Encounter Month)`,
        level: 'facilities',
        onClick: handleBackToFacilities,
      });
    }

    if (currentFacility) {
      const facilityName = typeof currentFacility === 'object' 
        ? currentFacility.facility_name || currentFacility.name
        : 'Facility';
      crumbs.push({
        label: facilityName,
        level: currentStatus === 3 ? 'batches' : 'claims',
        onClick: currentStatus === 3 ? handleBackToBatches : handleBackToFacilities,
      });
    }

    if (currentStatus === 3 && currentBatch) {
      crumbs.push({
        label: `Batch: ${currentBatch}`,
        level: 'batch_claims',
      });
    }

    setBreadcrumbs(crumbs);
  }, [currentStatus, currentMonth, currentFacility, currentBatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Render appropriate component based on navigation state
  const renderContent = () => {
    // Level 4: Batch Claims (Paid status only)
    if (currentStatus === 3 && currentMonth && currentFacility && currentBatch) {
      return (
        <PaymentBatchClaims
          month={getMonthKey(currentMonth) || currentMonth}
          facilityId={typeof currentFacility === 'object' ? currentFacility.facility_id : currentFacility}
          paymentBatch={currentBatch}
          facilityName={typeof currentFacility === 'object' ? currentFacility.facility_name : null}
          onClaimClick={handleClaimClick}
        />
      );
    }

    // Level 3b: Payment Batches (Paid status only)
    if (currentStatus === 3 && currentMonth && currentFacility && !currentBatch) {
      return (
        <PaymentBatches
          month={getMonthKey(currentMonth) || currentMonth}
          facilityId={typeof currentFacility === 'object' ? currentFacility.facility_id : currentFacility}
          facilityName={typeof currentFacility === 'object' ? currentFacility.facility_name : null}
          onBatchSelect={handleBatchSelect}
        />
      );
    }

    // Level 3a: Claims (Pending statuses only)
    if ((currentStatus === 0 || currentStatus === 1 || currentStatus === 2) && currentMonth && currentFacility) {
      return (
        <PaymentClaims
          segment={currentStatus}
          month={getMonthKey(currentMonth) || currentMonth}
          facilityId={typeof currentFacility === 'object' ? currentFacility.facility_id : currentFacility}
          facilityName={typeof currentFacility === 'object' ? currentFacility.facility_name : null}
          onClaimClick={handleClaimClick}
        />
      );
    }

    // Level 2: Facilities
    if (currentStatus !== null && currentMonth && !currentFacility) {
      return (
        <PaymentFacilities
          segment={currentStatus}
          month={getMonthKey(currentMonth) || currentMonth}
          onFacilitySelect={handleFacilitySelect}
        />
      );
    }

    // Level 1: Months
    if (currentStatus !== null && !currentMonth) {
      return (
        <PaymentStatusMonths
          segment={currentStatus}
          onMonthSelect={handleMonthSelect}
        />
      );
    }

    // The stage tab strip always keeps a stage selected, so this is unreachable
    // in practice — kept as a safe fallback.
    return (
      <PaymentStatusMonths
        segment={currentStatus}
        onMonthSelect={handleMonthSelect}
      />
    );
  };

  return (
    <div className="payment-hierarchy">
      <PaymentStageTabs activeStatus={currentStatus} onSelect={handleStatusSelect} />
      <DrilldownCrumbs breadcrumbs={breadcrumbs} />
      <div className="payment-hierarchy__content">
        {renderContent()}
      </div>
    </div>
  );
};

export default PaymentHierarchy;

